import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { createOrder, updateOrder } from '../../../common/api/clients/order.api';
import { queries } from '../../../common/api/queryKeys/queries';
import { TranslationKeyEn } from '../../../common/helpers/translate.helper';
import { OrderModel, OrderState } from '../../model/OrderModel';
import { FormikCreateEnquiryValues } from '../CreateEnquiryFlow';
import { buildOrderRequest } from '../util/buildOrderRequest';
import { waitForDirectOrderOffer } from '../util/waitForDirectOrderOffer';

interface DirectOrderCallbacks {
    onSuccess: (offerId: string) => void | Promise<void>;
    onError: (message: TranslationKeyEn, enquiryId?: string) => void | Promise<void>;
}

export const useDirectOrder = (orderId?: string) => {
    const queryClient = useQueryClient();
    const [isPending, setIsPending] = useState(false);
    const operation = useRef<AbortController | null>(null);

    useEffect(() => () => operation.current?.abort(), []);

    const placeDirectOrder = async (values: FormikCreateEnquiryValues, callbacks: DirectOrderCallbacks) => {
        if (operation.current) {
            return;
        }

        const controller = new AbortController();
        operation.current = controller;
        setIsPending(true);
        const { signal } = controller;
        let activeOrderId = orderId;
        let chosenOfferId: string | undefined;
        let recoveryMessage: TranslationKeyEn = 'order.directOrder.enquiryCreatedOrderingFailed';

        const fetchOrder = (): Promise<OrderModel> => {
            if (!activeOrderId) {
                throw new Error('Order has not been created');
            }
            return queryClient.fetchQuery({ ...queries.orders.detail(activeOrderId), staleTime: 0, retry: false });
        };

        const refresh = () => {
            void queryClient.invalidateQueries({ queryKey: queries.orders._def });
            void queryClient.invalidateQueries({ queryKey: queries.offers._def });
        };

        const handleOrdered = async (offerId: string) => {
            refresh();
            await callbacks.onSuccess(offerId);
        };

        try {
            const order = buildOrderRequest(values, OrderState.OPEN);
            if (activeOrderId) {
                await updateOrder({ orderId: activeOrderId, order });
            } else {
                const createdOrder = await createOrder(order);
                activeOrderId = createdOrder.id;
            }
            signal.throwIfAborted();

            const selectedOffer = await waitForDirectOrderOffer({
                queryClient,
                orderId: activeOrderId,
                customerId: values.customerId,
                supplierId: values.receiverSupplierIds[0],
                signal,
            });
            signal.throwIfAborted();
            chosenOfferId = selectedOffer.id;

            const closedOrder = await updateOrder({
                orderId: activeOrderId,
                order: { ...order, state: OrderState.CLOSED, chosenOfferId },
            });
            signal.throwIfAborted();
            if (closedOrder.state !== OrderState.CLOSED || closedOrder.chosenOfferId !== chosenOfferId) {
                throw new Error('Order placement was not confirmed');
            }
            await handleOrdered(chosenOfferId);
        } catch {
            if (signal.aborted) {
                return;
            }
            if (!activeOrderId) {
                await callbacks.onError('order.directOrder.couldNotStartDirectOrderCheckForm');
                return;
            }
            try {
                const currentOrder = await fetchOrder();
                signal.throwIfAborted();
                if (
                    chosenOfferId &&
                    currentOrder.state === OrderState.CLOSED &&
                    currentOrder.chosenOfferId === chosenOfferId
                ) {
                    await handleOrdered(chosenOfferId);
                    return;
                }
                if (currentOrder.state === OrderState.DRAFT) {
                    await callbacks.onError('order.directOrder.couldNotStartDirectOrderCheckForm');
                    return;
                }
            } catch {
                if (signal.aborted) {
                    return;
                }
                recoveryMessage = 'order.directOrder.couldNotConfirmOrderStatus';
            }
            refresh();
            await callbacks.onError(recoveryMessage, activeOrderId);
        } finally {
            if (!signal.aborted) {
                operation.current = null;
                setIsPending(false);
            }
        }
    };

    return { placeDirectOrder, isPending };
};
